import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function MessageComposeDialog({ open, onOpenChange, defaultClientId, onSent }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: defaultClientId || "",
    channel: "email",
    direction: "outbound",
    body: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data));
    setForm((prev) => ({ ...prev, client_id: defaultClientId || prev.client_id }));
    // eslint-disable-next-line
  }, [open, defaultClientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.body.trim()) {
      toast.error("Danışan ve mesaj zorunludur");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/messages", form);
      toast.success("Mesaj kaydedildi — kanal entegrasyonu yapılana kadar sistem içi loglanır");
      onSent && onSent(data);
      onOpenChange(false);
      setForm({ client_id: defaultClientId || "", channel: "email", direction: "outbound", body: "" });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="msg-compose-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Mesaj</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Danışan *</label>
            <select
              required
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              data-testid="msg-client"
              className="w-full h-9 px-2 border border-pleiades-border rounded-sm text-[13px] bg-white"
            >
              <option value="">Seçin…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Kanal</label>
            <div className="grid grid-cols-3 gap-2">
              {[["email", "E-posta"], ["whatsapp", "WhatsApp"], ["sms", "SMS"]].map(([k, v]) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setForm({ ...form, channel: k })}
                  data-testid={`msg-channel-${k}`}
                  className={"h-9 border rounded-sm text-[12px] " + (form.channel === k ? "border-pleiades-blue text-pleiades-blue bg-pleiades-blue-soft" : "border-pleiades-border hover:bg-pleiades-sidebar")}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Mesaj *</label>
            <textarea
              required
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="msg-body"
              className="w-full text-[13px] border border-pleiades-border rounded-sm p-2 focus:outline-none focus:border-pleiades-blue resize-none"
              placeholder="Mesajınızı yazın…"
            />
            <p className="text-[11px] text-pleiades-muted mt-1">
              Bu mesaj sistem içi zaman çizelgesine kaydedilir. SendGrid/Twilio entegrasyonu tamamlandığında gerçek gönderim otomatik yapılır.
            </p>
          </div>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 px-3 border border-pleiades-border rounded-sm text-[13px] hover:bg-pleiades-sidebar">
              Vazgeç
            </button>
            <button type="submit" disabled={busy} data-testid="msg-submit" className="h-9 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[13px] rounded-sm disabled:opacity-50">
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
